import Guard from 'arg-guard'

import Logger from '@/logger'

import Service from './service'
import ServiceOperation from './service-operation'

import ServiceBasket from '@/models/service-basket'
import ServiceBasketItem from '@/models/service-basket-item'

/**
 * Gateway for accessing service basket data.
 * @export ServiceBasketService
 * @class ServiceBasketService
 */
export default class ServiceBasketService extends Service {

    /**
     * Creates an instance of ServiceBasketService.
     */
    constructor() {
        super('/service-basket', 'Service Basket')
    }

    /**
     * Build the URL path for a basket item.
     * @private
     * @param {number} addressId The address ID the item is linked to.
     * @param {number} typeId The type ID the item is linked to.
     * @returns {string}
     */
    _getUrlPath(addressId, typeId) {
        return `item/for-address/${ addressId }/of-type/${ typeId }`
    }

    /**
     * Deletes the specified service basket item.
     * @async
     * @param {number} addressId The address identifier.
     * @param {number} serviceTypeId The service type identifier.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ServiceBasketService
     */
    async deleteServiceBasketItemAsync(addressId, serviceTypeId) {
        const methodName = 'deleteServiceBasketItemAsync'
        Logger.trackMethod(methodName, 'ServiceBasketService', { addressId, serviceTypeId })
        Guard.throwIf(addressId, 'addressId')
        Guard.throwIf(serviceTypeId, 'serviceTypeId')

        const operationState = new ServiceOperation('Delete Service Basket Item', true)
        const url = this._getUrlPath(addressId, serviceTypeId)
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [DELETE]')

        return await this._processDelete(url, operationState, methodName)
    }

    /**
     * Loads the requested service basket.
     * @async
     * @param {number} addressId The address identifier.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ServiceBasketService
     */
    async loadServiceBasketAsync(addressId) {
        const methodName = 'loadServiceBasketAsync'
        Logger.trackMethod(methodName, 'ServiceBasketService', { addressId })
        Guard.throwIf(addressId, 'addressId')

        const operationState = new ServiceOperation('Load Service Basket', true)
        const url = `for-address/${ addressId }`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        return await this._processGet(url, operationState, methodName,
            (data) => ServiceBasket.ensure(data))
    }

    /**
     * Loads the requested service basket item.
     * @async
     * @param {number} addressId The address identifier.
     * @param {number} serviceTypeId The service type identifier.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ServiceBasketService
     */
    async loadServiceBasketItemAsync(addressId, serviceTypeId) {
        const methodName = 'loadServiceBasketItemAsync'
        Logger.trackMethod(methodName, 'ServiceBasketService', { addressId, serviceTypeId })
        Guard.throwIf(addressId, 'addressId')
        Guard.throwIf(serviceTypeId, 'serviceTypeId')

        const operationState = new ServiceOperation('Load Service Basket Item', true)
        const url = this._getUrlPath(addressId, serviceTypeId)
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        return await this._processGet(url, operationState, methodName,
            (data) => ServiceBasketItem.ensure(data))
    }

    /**
     * Upserts the provided service basket item.
     * @async
     * @param {ServiceBasketItem} data
     * @param {?number} renovationId The optional ID of the renovation - if not provided server defaults are used.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof ServiceBasketService
     */
    async upsertServiceBasketItemAsync(data, renovationId = null) {
        const methodName = 'upsertServiceBasketItemAsync'
        Logger.trackMethod(methodName, 'ServiceBasketService', { data, renovationId })
        Guard.throwIf(data, 'data')

        const operationState = new ServiceOperation('Upsert Service Basket Item', true)
        const url = renovationId
            ? `${ this._getUrlPath(data.addressId, data.serviceTypeId) }/from-renovation/${ renovationId }`
            : this._getUrlPath(data.addressId, data.serviceTypeId)

        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [PUT]')
        return await this._processPut(url, data, operationState, methodName)
    }
}
